/**
 * Sign-in.
 *
 * Two details that are easy to get wrong and matter here:
 *
 * - The error message is whatever the server said, and the server deliberately
 *   returns the same text for "no such account" and "wrong password". Adding a
 *   friendlier client-side distinction would reintroduce the enumeration oracle
 *   the API was careful to close.
 * - `?next=` is honoured, but only for internal paths.
 */

import { useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { Logo } from '@/components/ui/icons'
import { Alert, Button, Field, Input, Panel } from '@/components/ui/primitives'
import { ApiError } from '@/services/client'
import { useAuth } from '@/stores/auth'

export function LoginPage() {
  const [params] = useSearchParams()
  const { login, isAuthenticated, isStaff, loading } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<ApiError | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // An open redirect is a phishing primitive: only same-origin paths are used.
  const rawNext = params.get('next') ?? '/'
  const next = rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '/'

  // ONE redirect path, declarative.
  //
  // The obvious implementation — a `<Navigate>` guard for the
  // already-signed-in case, plus an imperative `navigate()` after a successful
  // submit — has both fire on the same login. `setUser` re-renders this
  // component while it is still mounted, the guard sees `isAuthenticated` and
  // redirects to `next`, and that overrides the imperative navigation that had
  // just sent a staff user to /admin. Computing the destination once, in one
  // place, removes the race rather than papering over it.
  if (!loading && isAuthenticated) {
    const destination = next !== '/' ? next : isStaff ? '/admin' : '/'
    return <Navigate to={destination} replace />
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(email, password)
      // No navigate() here: the guard above redirects on the next render, now
      // that the user is known.
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--surface-page)] px-4 py-12">
      <div className="w-full max-w-sm">
        <Link to="/" className="mb-6 flex items-center justify-center gap-2.5">
          <Logo size={30} />
          <span className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">
            Naija<span className="text-[var(--accent-text)]">Index</span>
          </span>
        </Link>

        <Panel raised className="p-6">
          <h1 className="text-base font-semibold text-[var(--text-primary)]">Sign in</h1>
          <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
            For business owners and operations staff.
          </p>

          <form onSubmit={submit} className="mt-5 space-y-4">
            <Field label="Email" htmlFor="email" errors={error?.fields.email} required>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                invalid={Boolean(error?.fields.email)}
              />
            </Field>

            <Field label="Password" htmlFor="password" errors={error?.fields.password} required>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                invalid={Boolean(error?.fields.password)}
              />
            </Field>

            {error && !Object.keys(error.fields).length ? (
              <Alert tone={error.code === 'account_locked' ? 'caution' : 'negative'}>
                {error.message}
              </Alert>
            ) : null}

            <Button type="submit" loading={submitting} className="w-full">
              Sign in
            </Button>
          </form>
        </Panel>

        <p className="mt-5 text-center text-xs text-[var(--text-secondary)]">
          Browsing the index needs no account.{' '}
          <Link to="/" className="text-[var(--accent-text)] hover:underline">
            Back to search
          </Link>
        </p>
      </div>
    </div>
  )
}
